// src/pages/NavBar.jsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography,
  Drawer,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SearchIcon from '@mui/icons-material/Search';
import MenuIcon from '@mui/icons-material/Menu';
import { useNavigate } from 'react-router-dom';
import commands from '../data/commands';
import DocsSidebar from 'components/DocsSidebar';

function NavBar({ toggleTheme, isDarkMode }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [searchValue, setSearchValue] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);

  const filteredCommands = searchValue
    ? commands.filter((cmd) => cmd.name.includes(searchValue))
    : [];

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: 60,
        backgroundColor: '#1D1D27',
        color: 'white',
        padding: '10px 20px',
        zIndex: 10,
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 2,
      }}
    >
      {/* 로고 */}
      <Box sx={{ display: 'flex', alignItems: 'center', ml:{ xs: '0.1%', sm: '5%', md: '18%' } }}>
        <a href="/" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
          <img src="/navlogo.png" alt="전적이" style={{ height: 40, marginRight: 10 }} />
        </a>
      </Box>

      {/* PC 전용 버튼 */}
      {!isMobile && (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button href="/docs" sx={navBtnStyle}>명령어</Button>
          <Button href="/invite" target="_blank" sx={navBtnStyle}>초대하기</Button>
          <Button href="/vote" sx={navBtnStyle}>추천하기</Button>
          <Button href="/discord" target="_blank" sx={navBtnStyle}>커뮤니티</Button>
        </Box>
      )}

      {/* 오른쪽: 검색창 + 다크모드 + 햄버거 */}
      <Box sx={{ ml: 'auto', mr: { xs: '0.1%', sm: '5%', md: '18%' }, display: 'flex', alignItems: 'center', gap: 1, position: 'relative' }}>
        {/* 검색창 */}
        {!isMobile && (
          <Box sx={{ position: 'relative' , mr: {md: 2,}, }}>
            <TextField
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              placeholder="명령어 검색"
              size="small"
              variant="outlined"
              sx={{
                backgroundColor: '#2B2B3C',
                borderRadius: 2,
                width: 220,
                input: { color: 'white' },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: 'gray' }} />
                  </InputAdornment>
                ),
              }}
            />

            {filteredCommands.length > 0 && (
              <Paper
                sx={{
                  position: 'absolute',
                  top: 40,
                  left: 0,
                  width: 250,
                  maxHeight: 300,
                  overflowY: 'auto',
                  backgroundColor: '#2B2B3C',
                  color: 'white',
                  borderRadius: 2,
                  boxShadow: 5,
                  mt: 0.5,
                  py: 1,
                }}
              >
                <List disablePadding>
                  {filteredCommands.map((cmd) => {
                    const label = cmd.name.replace(/^\/+/g, '') + ' 명령어';
                    const category = cmd.category || '기타';
                    return (
                      <ListItem
                        key={cmd.path}
                        button
                        onClick={() => {
                          navigate(cmd.path);
                          setSearchValue('');
                        }}
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'flex-start',
                          px: 2,
                          py: 1,
                          '&:hover': {
                            backgroundColor: '#8296ef',
                            color: '#fff',
                          },
                        }}
                      >
                        <Typography variant="caption" sx={{ opacity: 0.6, fontSize: '0.7rem' }}>{category} 명령어</Typography>
                        <Typography variant="body2">{label}</Typography>
                      </ListItem>
                    );
                  })}
                </List>
              </Paper>
            )}
          </Box>
        )}

        {/* 다크모드 토글 */}
        <IconButton onClick={toggleTheme} color="inherit">
          {isDarkMode ? <LightModeIcon /> : <DarkModeIcon />}
        </IconButton>

        {/* 햄버거 메뉴 */}
        {isMobile && (
          <IconButton onClick={toggleDrawer} color="inherit">
            <MenuIcon />
          </IconButton>
        )}
      </Box>

      {/* 드로어 (모바일 전용) */}
      <Drawer anchor="right" open={drawerOpen} onClose={toggleDrawer}>
      <Box
        sx={{
          width: 280,
          p: 2,
          backgroundColor: '#36393f', // ✅ 드로어 배경색 설정!
          height: '100%',              // ✅ 전체 높이 채우기 (중요)
        }}
      >
          <TextField
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder="명령어 검색"
            size="small"
            fullWidth
            sx={{
              mb: 2,
              backgroundColor: '#2B2B3C',
              borderRadius: 1,
              input: { color: 'white' },
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon sx={{ color: 'gray' }} />
                </InputAdornment>
              ),
            }}
          />
          {filteredCommands.length > 0 && (
            <Paper
              sx={{
                mb: 2,
                backgroundColor: '#2B2B3C',
                color: 'white',
                borderRadius: 2,
                maxHeight: 300,
                overflowY: 'auto',
                boxShadow: 5,
                py: 1,
              }}
            >
              <List disablePadding>
                {filteredCommands.map((cmd) => {
                  const label = cmd.name.replace(/^\/+/, '') + ' 명령어';
                  const category = cmd.category || '기타';

                  return (
                    <ListItem
                      key={cmd.path}
                      button
                      onClick={() => {
                        navigate(cmd.path);
                        setSearchValue('');
                        toggleDrawer();
                      }}
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        px: 2,
                        py: 1.2,
                        '&:hover': {
                          backgroundColor: '#8296ef',
                          color: '#fff',
                        },
                      }}
                    >
                      <Typography
                        variant="caption"
                        sx={{
                          opacity: 0.6,
                          fontSize: '0.7rem',
                          lineHeight: 1.2,
                        }}
                      >
                        {category} 명령어
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 500,
                        }}
                      >
                        {label}
                      </Typography>
                    </ListItem>
                  );
                })}
              </List>
            </Paper>
          )}

          <List>
            {[
              { label: '명령어', href: '/docs' },
              { label: '초대하기', href: '/invite', newTab: true },
              { label: '추천하기', href: '/vote' },
              { label: '커뮤니티', href: '/discord', newTab: true },
            ].map((item) => (
              <ListItem
                key={item.href}
                button
                component="a"
                href={item.href}
                target={item.newTab ? '_blank' : undefined} // ✅ 새창 조건부 적용
                rel={item.newTab ? 'noopener noreferrer' : undefined} // ✅ 보안 속성
                sx={{
                  px: 2,
                  py: 1.2,
                  borderRadius: 1,
                  color: 'white',
                  '&:hover': {
                    backgroundColor: '#8296ef',
                    color: '#fff',
                  },
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{ fontWeight: 'bold' }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  );
}

const navBtnStyle = {
  color: 'white',
  textTransform: 'none',
  '&:hover': { backgroundColor: '#8296ef', fontWeight: 'bold' },
  fontSize: {
    xs: '1rem',  // 모바일
    sm: '0.85rem',    // 태블릿
    md: '1rem',  // 데스크탑
  },
};

export default NavBar;
